import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Button from '../components/Button';
import Input from '../components/Input';
import Modal from '../components/Modal';
import { publicEventsService, PublicEventDetail } from '../services/publicEventsService';
import { orderService, InitOrderResponse, OrderQuote, EventNotOnSaleError } from '../services/orderService';
import { createPurchaseSecret, rememberPurchaseSecret } from '../utils/purchaseSecret';
import { formatEventDate } from '../utils/dates';

const RAW_CATEGORIES = [
    { id: 'ALL', label: 'Todos', emoji: '🎭' },
    { id: 'FESTIVAL', label: 'Festivales', emoji: '🎉' },
    { id: 'CONCERT', label: 'Conciertos', emoji: '🎵' },
    { id: 'SPORTS', label: 'Deportes', emoji: '🏅' },
    { id: 'STANDUP', label: 'Stand-up', emoji: '🎤' },
    { id: 'THEATER', label: 'Teatro', emoji: '🎪' },
    { id: 'ART', label: 'Arte', emoji: '🖼️' },
    { id: 'FAMILY', label: 'Familiares', emoji: '👨🏻‍👩🏻‍👧🏻‍👦🏻' },
    { id: 'CONFERENCE', label: 'Conferencias', emoji: '💼' },
    { id: 'OTHER', label: 'Otros', emoji: '🫂' },
];

const BoldPaymentWidget = ({ amount, orderId, integritySignature, apiKey, description, customerData, redirectionUrl }: any) => {
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (containerRef.current) {
            containerRef.current.innerHTML = '';

            const script = document.createElement('script');
            script.src = "https://checkout.bold.co/library/boldPaymentButton.js";

            script.setAttribute('data-bold-button', 'dark-L');
            script.setAttribute('data-order-id', orderId);
            script.setAttribute('data-currency', 'COP');
            script.setAttribute('data-amount', amount.toString());
            script.setAttribute('data-api-key', apiKey);

            if (integritySignature) {
                script.setAttribute('data-integrity-signature', integritySignature);
            }

            script.setAttribute('data-redirection-url', redirectionUrl);
            script.setAttribute('data-description', description);

            if (customerData) {
                script.setAttribute('data-customer-data', JSON.stringify(customerData));
            }

            script.setAttribute('data-render-mode', 'embedded');
            containerRef.current.appendChild(script);
        }
    }, [amount, orderId, apiKey, integritySignature, description, customerData, redirectionUrl]);

    return (
        <div className="flex flex-col items-center justify-center p-4 border border-gray-100 rounded-xl bg-white shadow-sm w-full">
            <span className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-3">Opción 1</span>
            <div ref={containerRef} />
        </div>
    );
};

const WompiPaymentWidget = ({ amount, reference, integritySignature, publicKey, currency, redirectionUrl, customerData }: any) => {
    const formRef = useRef<HTMLFormElement>(null);

    useEffect(() => {
        if (formRef.current) {
            formRef.current.innerHTML = '';

            const script = document.createElement('script');
            script.src = "https://checkout.wompi.co/widget.js";

            script.setAttribute('data-render', 'button');
            script.setAttribute('data-public-key', publicKey);
            script.setAttribute('data-currency', currency);
            script.setAttribute('data-amount-in-cents', (amount * 100).toString());
            script.setAttribute('data-reference', reference);
            script.setAttribute('data-signature:integrity', integritySignature);
            script.setAttribute('data-redirect-url', redirectionUrl);

            if (customerData?.email) {
                script.setAttribute('data-customer-data:email', customerData.email);
            }
            if (customerData?.phone) {
                script.setAttribute('data-customer-data:phone-number', customerData.phone);
                script.setAttribute('data-customer-data:phone-number-prefix', '+57');
            }

            formRef.current.appendChild(script);
        }
    }, [amount, reference, integritySignature, publicKey, currency, redirectionUrl, customerData]);

    return (
        <div className="flex flex-col items-center justify-center p-4 border border-gray-100 rounded-xl bg-white shadow-sm w-full">
            <span className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-3">Pagar con Wompi</span>
            <form ref={formRef}></form>
        </div>
    );
};

// Checkout Pro: the preference is created by the backend, so the buyer only needs to be redirected.
// Mercado Pago returns to /payment?mp-order-id=<reservationId> (back_urls set by ms-payments-integration).
const MercadoPagoPaymentWidget = ({ checkoutUrl }: { checkoutUrl: string }) => (
    <div className="flex flex-col items-center justify-center p-4 border border-gray-100 rounded-xl bg-white shadow-sm w-full">
        <span className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-3">Pagar con Mercado Pago</span>
        <a
            href={checkoutUrl}
            className="flex items-center justify-center gap-2 w-full max-w-xs rounded-lg bg-[#009EE3] hover:bg-[#0089C7] px-4 py-3 text-white font-semibold transition-colors"
        >
            <img src="/Mercadopago_logo.png" alt="" className="h-5 w-auto bg-white rounded p-0.5" />
            Pagar con Mercado Pago
        </a>
    </div>
);

// SuperTicket does not handle refunds: the organizer receives the money through its own gateway
const RefundNotice = ({ email, phone }: { email?: string; phone?: string }) => {
    const contacts = [email, phone].filter(Boolean).join(' · ');
    return (
        <p className="text-xs text-[var(--st-text-muted)]">
            Los reembolsos los gestiona directamente el organizador del evento
            {contacts ? <>: <span className="font-medium text-[var(--st-text-secondary)]">{contacts}</span></> : '.'}
        </p>
    );
};

interface TransactionDataPayload {
    orderResponse: InitOrderResponse;
    description: string;
    boldRedirectionUrl: string;
    wompiRedirectionUrl: string;
    customerData: {
        email: string;
        phone: string;
    };
}

const EventDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [event, setEvent] = useState<PublicEventDetail | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [selectedStage, setSelectedStage] = useState<string | null>(null);
    const [ticketQuantity, setTicketQuantity] = useState(1);
    const [buyerData, setBuyerData] = useState({ email: '', phone: '' });

    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [transactionData, setTransactionData] = useState<TransactionDataPayload | null>(null);

    // Tickets + service fee = total, computed by the backend for the current selection
    const [quote, setQuote] = useState<OrderQuote | null>(null);
    const [isQuoting, setIsQuoting] = useState(false);
    const [quoteError, setQuoteError] = useState<string | null>(null);
    const [salesClosed, setSalesClosed] = useState(false);

    useEffect(() => {
        const fetchEvent = async () => {
            if (!id) return;
            setIsLoading(true);
            try {
                const data = await publicEventsService.getEventById(id);
                setEvent(data);
                const firstActive = data.stages.find(s => s.isActive);
                if (firstActive) setSelectedStage(firstActive.id);
            } catch (err) {
                setError("No pudimos encontrar el evento que buscas.");
            } finally {
                setIsLoading(false);
            }
        };
        fetchEvent();
    }, [id]);

    useEffect(() => {
        setTicketQuantity(1);
    }, [selectedStage]);

    useEffect(() => {
        if (!event || !selectedStage) {
            setQuote(null);
            return;
        }
        let cancelled = false;
        setIsQuoting(true);
        setQuoteError(null);
        // Short delay so quickly pressing + / - asks for one quote, not one per click
        const timer = setTimeout(async () => {
            try {
                const result = await orderService.quoteOrder(event.id, [{ stageId: selectedStage, quantity: ticketQuantity }]);
                if (!cancelled) setQuote(result);
            } catch (err) {
                if (cancelled) return;
                setQuote(null);
                if (err instanceof EventNotOnSaleError) {
                    setSalesClosed(true);
                } else {
                    setQuoteError('No pudimos calcular el cargo por servicio. Intenta de nuevo.');
                }
            } finally {
                if (!cancelled) setIsQuoting(false);
            }
        }, 300);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [event, selectedStage, ticketQuantity]);

    const handleBuyerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setBuyerData(prev => ({ ...prev, [name]: value }));
    };

    const adjustQuantity = (delta: number) => {
        setTicketQuantity(prev => Math.max(1, prev + delta));
    };

    // The event's wall time, shown as the organizer typed it
    const formatDate = (dateString: string) => ({
        day: formatEventDate(dateString, { day: 'numeric' }),
        month: formatEventDate(dateString, { month: 'short' }).toUpperCase(),
        full: formatEventDate(dateString, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    });

    const formatPrice = (price: number) => {
        return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(price);
    };

    const getSelectedStagePrice = () => {
        if (!event || !selectedStage) return 0;
        return event.stages.find(s => s.id === selectedStage)?.price || 0;
    };

    const handleInitiatePurchase = async () => {
        if (!event || !selectedStage) return;

        setIsProcessing(true);
        const stageName = event.stages.find(s => s.id === selectedStage)?.name;

        try {
            // Only this tab will be able to see the tickets once paid; the gateways return the buyer to it
            const purchaseSecret = await createPurchaseSecret();
            const orderResponse = await orderService.initOrder({
                eventId: event.id,
                customerEmail: buyerData.email,
                customerPhone: buyerData.phone,
                items: [
                    {
                        stageId: selectedStage,
                        quantity: ticketQuantity
                    }
                ],
                purchaseSecretHash: purchaseSecret?.hash
            });
            if (purchaseSecret) rememberPurchaseSecret(orderResponse.reservationId, purchaseSecret.secret);

            setTransactionData({
                orderResponse: orderResponse,
                description: `Entradas ${event.name} (${stageName})`,
                boldRedirectionUrl: window.location.origin + "/payment",
                wompiRedirectionUrl: window.location.origin + `/payment?wompi-order-id=${orderResponse.reservationId}`,
                customerData: {
                    email: buyerData.email,
                    phone: buyerData.phone
                }
            });

            setIsPaymentModalOpen(true);

        } catch (err) {
            if (err instanceof EventNotOnSaleError) {
                setSalesClosed(true);
            } else {
                alert("Hubo un problema al procesar tu orden. Por favor intenta de nuevo.");
            }
        } finally {
            setIsProcessing(false);
        }
    };

    if (isLoading) {
        return <div className="st-page flex items-center justify-center min-h-[60vh]"><div className="st-spinner st-spinner-xl"></div></div>;
    }

    if (error || !event) {
        return (
            <div className="st-page flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
                <div className="text-6xl mb-4">😕</div>
                <h1 className="font-display text-3xl font-bold text-[var(--st-text-primary)] mb-2">Ups, algo salió mal</h1>
                <p className="text-[var(--st-text-secondary)] mb-6">{error || "Evento no encontrado"}</p>
                <Button variant="secondary" onClick={() => navigate('/')}>Volver al Inicio</Button>
            </div>
        );
    }

    const dateObj = formatDate(event.startDate);
    const categoryConfig = RAW_CATEGORIES.find(c => c.id === event.category);

    return (
        <div className="st-page pb-20">
            <div className="relative w-full bg-[#0f172a] overflow-hidden mb-4">
                <div className="absolute inset-0 opacity-40">
                    <img src={event.imageUrl} alt="" className="w-full h-full object-cover blur-xl scale-110" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a] via-[#0f172a]/60 to-transparent"></div>
                <div className="st-container relative z-10 pt-20 pb-12 md:pt-32 md:pb-16">
                    <div className="flex flex-col md:flex-row gap-8 items-end">
                        <div className="w-full max-w-[300px] aspect-[3/4] rounded-2xl shadow-2xl overflow-hidden border-4 border-white/10 hidden md:block mx-auto md:mx-0">
                            <img src={event.imageUrl} alt={event.name} className="w-full h-full object-cover" />
                        </div>
                        <div className="flex-1 text-center md:text-left">
                            <span className="st-badge st-badge-primary mb-4 bg-white/10 text-white border-none backdrop-blur-md">
                                {categoryConfig?.emoji || '📅'} {categoryConfig?.label || event.category}
                            </span>
                            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
                                {event.name}
                            </h1>
                            <div className="flex flex-col md:flex-row items-center gap-4 md:gap-8 text-gray-300 text-lg">
                                <div className="flex items-center gap-2">
                                    <div className="p-2 rounded-lg bg-white/10 backdrop-blur-sm">
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                    </div>
                                    <span>{dateObj.full}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="p-2 rounded-lg bg-white/10 backdrop-blur-sm">
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                    </div>
                                    <span>{event.location}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="st-container -mt-8 relative z-20">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                    <div className="lg:col-span-7 flex flex-col gap-8">
                        <div className="md:hidden rounded-2xl overflow-hidden shadow-lg mb-6">
                            <img src={event.imageUrl} alt={event.name} className="w-full h-auto" />
                        </div>
                        <div className="st-card-static p-6 md:p-8 bg-white">
                            <h2 className="font-display text-2xl font-bold mb-4 text-[var(--st-text-primary)]">Acerca del Evento</h2>
                            <p className="text-[var(--st-text-secondary)] leading-relaxed whitespace-pre-line text-lg">
                                {event.description}
                            </p>
                        </div>
                        <div className="st-card-static p-6 md:p-8 bg-white">
                            <div className="flex items-start gap-4">
                                <div className="p-3 rounded-full bg-[var(--st-info-light)] text-[var(--st-info)] shrink-0">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                </div>
                                <div>
                                    <h3 className="font-display text-xl font-bold mb-2 text-[var(--st-text-primary)]">Información Importante</h3>
                                    <p className="text-sm text-[var(--st-text-secondary)] whitespace-pre-line">
                                        {event.termsConditions || "No se especificaron términos adicionales para este evento."}
                                    </p>
                                    <div className="mt-3">
                                        <RefundNotice email={event.refundContactEmail} phone={event.refundContactPhone} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-5">
                        <div className="sticky top-24 flex flex-col gap-6">
                            <div className="st-card-static p-6 bg-white shadow-xl border-[var(--st-accent-primary-light)] mt-8">
                                <h3 className="font-display text-xl font-bold mb-4 flex items-center gap-2">
                                    🎟️ Entradas
                                </h3>

                                <div className="space-y-3 mb-6">
                                    {event.stages.map((stage) => (
                                        <div
                                            key={stage.id}
                                            onClick={() => stage.isActive && setSelectedStage(stage.id)}
                                            className={`
                                                relative p-4 pr-12 rounded-xl border-2 cursor-pointer transition-all
                                                ${!stage.isActive ? 'opacity-60 grayscale cursor-not-allowed bg-gray-50 border-gray-100' :
                                                selectedStage === stage.id ? 'border-[var(--st-accent-primary)] bg-[var(--st-accent-primary-light)]' : 'border-[var(--st-border)] hover:border-[var(--st-accent-primary)] bg-white'}
                                            `}
                                        >
                                            <div className="flex justify-between items-center mb-1 flex-wrap">
                                                <span className="font-bold text-[var(--st-text-primary)] mr-2">{stage.name}</span>
                                                {stage.isActive ? (
                                                    <span className="font-display font-bold text-lg text-[var(--st-accent-primary)] whitespace-nowrap leading-none translate-y-0.5">
                                                        {formatPrice(stage.price)}
                                                    </span>
                                                ) : (
                                                    <span className="st-badge st-badge-neutral text-xs">No disponible</span>
                                                )}
                                            </div>
                                            <p className="text-xs text-[var(--st-text-muted)]">
                                                {stage.isActive ? 'Disponibilidad inmediata' : 'Agotado o Inactivo'}
                                            </p>
                                            {stage.isActive && (
                                                <div className={`absolute top-1/2 -translate-y-1/2 right-4 w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedStage === stage.id ? 'border-[var(--st-accent-primary)]' : 'border-gray-300'}`}>
                                                    {selectedStage === stage.id && <div className="w-2.5 h-2.5 rounded-full bg-[var(--st-accent-primary)]"></div>}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                {selectedStage && (
                                    <div className="space-y-5 border-t border-[var(--st-border)] pt-5 animate-fade-in">
                                        <div className="flex items-center justify-between">
                                            <span className="font-medium text-[var(--st-text-primary)]">Cantidad</span>
                                            <div className="flex items-center gap-3 bg-[var(--st-bg-tertiary)] rounded-lg p-1">
                                                <button onClick={() => adjustQuantity(-1)} className="w-8 h-8 rounded-md bg-white shadow-sm flex items-center justify-center hover:text-[var(--st-accent-primary)] disabled:opacity-50 transition-colors" disabled={ticketQuantity <= 1}>-</button>
                                                <span className="font-display font-bold text-xl w-8 text-center">{ticketQuantity}</span>
                                                <button onClick={() => adjustQuantity(1)} className="w-8 h-8 rounded-md bg-white shadow-sm flex items-center justify-center hover:text-[var(--st-accent-primary)] transition-colors">+</button>
                                            </div>
                                        </div>
                                        <div className="bg-[var(--st-bg-secondary)] p-3 rounded-lg border border-[var(--st-border)] space-y-2">
                                            <div className="flex items-center justify-between text-sm">
                                                <span className="text-[var(--st-text-secondary)]">
                                                    Boletas ({ticketQuantity} × {formatPrice(getSelectedStagePrice())})
                                                </span>
                                                <span className="font-medium text-[var(--st-text-primary)]">
                                                    {formatPrice(quote ? quote.subtotal : getSelectedStagePrice() * ticketQuantity)}
                                                </span>
                                            </div>
                                            {quote?.feePayer === 'BUYER' && (
                                                <div className="flex items-center justify-between text-sm">
                                                    <span className="text-[var(--st-text-secondary)]">Cargo por servicio SuperTicket</span>
                                                    <span className="font-medium text-[var(--st-text-primary)]">{formatPrice(quote.serviceFee)}</span>
                                                </div>
                                            )}
                                            <div className="flex items-center justify-between border-t border-[var(--st-border)] pt-2">
                                                <span className="text-sm text-[var(--st-text-secondary)]">Total a pagar:</span>
                                                <span className="font-display font-bold text-xl text-[var(--st-accent-primary)]">
                                                    {isQuoting || !quote ? '…' : formatPrice(quote.total)}
                                                </span>
                                            </div>
                                            {quoteError && <p className="text-xs text-[var(--st-error)]">{quoteError}</p>}
                                        </div>
                                        <div className="space-y-3">
                                            <p className="text-xs font-bold uppercase text-[var(--st-text-muted)]">Datos de confirmación</p>
                                            <Input name="email" type="email" placeholder="Correo electrónico" value={buyerData.email} onChange={handleBuyerChange} />
                                            <Input name="phone" className="mb-6" type="tel" placeholder="Teléfono celular" value={buyerData.phone} onChange={handleBuyerChange} />
                                        </div>
                                    </div>
                                )}

                                <div className="space-y-3 text-center">
                                    {salesClosed && (
                                        <p className="text-sm font-medium text-[var(--st-error)]">Las ventas de este evento están cerradas.</p>
                                    )}
                                    <Button
                                        variant="primary"
                                        className="st-btn st-btn-primary st-btn-lg w-full"
                                        disabled={!selectedStage || !buyerData.email || !buyerData.phone || isProcessing || salesClosed || !quote}
                                        onClick={handleInitiatePurchase}
                                    >
                                        {isProcessing ? 'Procesando...' : 'Comprar Entradas'}
                                    </Button>
                                    <p className="text-xs text-center text-[var(--st-text-muted)]">
                                        Transacción procesada de forma segura.
                                        <br/>Al continuar aceptas los términos y condiciones.
                                    </p>
                                    <RefundNotice email={event.refundContactEmail} phone={event.refundContactPhone} />
                                </div>
                            </div>

                            <div className="st-card-static p-4 flex items-center gap-4 bg-[var(--st-bg-primary)]">
                                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                                </div>
                                <div>
                                    <p className="text-xs font-bold text-[var(--st-text-muted)] uppercase">Dudas del evento</p>
                                    <p className="font-medium text-[var(--st-text-primary)]">{event.whatsappContact}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Modal
                isOpen={isPaymentModalOpen}
                onClose={() => setIsPaymentModalOpen(false)}
                title="Selecciona un método de pago"
                className="max-w-md"
            >
                {transactionData && (
                    <div className="flex flex-col gap-4 bg-gray-50 p-4 rounded-xl">
                        <div className="bg-white border border-gray-100 rounded-xl p-4 space-y-1 text-sm">
                            <div className="flex justify-between">
                                <span className="text-[var(--st-text-secondary)]">Boletas</span>
                                <span>{formatPrice(transactionData.orderResponse.subtotal)}</span>
                            </div>
                            {transactionData.orderResponse.feePayer === 'BUYER' && (
                                <div className="flex justify-between">
                                    <span className="text-[var(--st-text-secondary)]">Cargo por servicio SuperTicket</span>
                                    <span>{formatPrice(transactionData.orderResponse.serviceFee)}</span>
                                </div>
                            )}
                            <div className="flex justify-between font-bold border-t border-gray-100 pt-1">
                                <span>Total a pagar</span>
                                <span className="text-[var(--st-accent-primary)]">{formatPrice(transactionData.orderResponse.amount)}</span>
                            </div>
                        </div>
                        {transactionData.orderResponse.boldIntegrity && (
                            <BoldPaymentWidget
                                amount={transactionData.orderResponse.amount}
                                orderId={transactionData.orderResponse.reservationId}
                                description={transactionData.description}
                                apiKey={transactionData.orderResponse.boldIntegrity.paymentPublicKey}
                                integritySignature={transactionData.orderResponse.boldIntegrity.integritySignature}
                                redirectionUrl={transactionData.boldRedirectionUrl}
                                customerData={transactionData.customerData}
                            />
                        )}

                        {transactionData.orderResponse.wompiIntegrity && (
                            <WompiPaymentWidget
                                amount={transactionData.orderResponse.amount}
                                reference={transactionData.orderResponse.reservationId}
                                currency={transactionData.orderResponse.currency}
                                publicKey={transactionData.orderResponse.wompiIntegrity.paymentPublicKey}
                                integritySignature={transactionData.orderResponse.wompiIntegrity.integritySignature}
                                redirectionUrl={transactionData.wompiRedirectionUrl}
                                customerData={transactionData.customerData}
                            />
                        )}

                        {transactionData.orderResponse.mercadoPagoCheckout && (
                            <MercadoPagoPaymentWidget
                                checkoutUrl={transactionData.orderResponse.mercadoPagoCheckout.checkoutUrl}
                            />
                        )}
                    </div>
                )}
            </Modal>

        </div>
    );
};

export default EventDetail;
